import { create } from 'zustand';

import type { AuthResponse, CurrentUser } from '../api/types';

/**
 * `unknown` – boot in progress (refresh cookie not yet checked);
 * `authenticated` – access token in memory;
 * `anonymous` – no valid session (login required).
 */
export type AuthStatus = 'unknown' | 'authenticated' | 'anonymous';

/** Why the session ended — lets the login page explain an unexpected sign-out. */
export type SignOutReason = 'logout' | 'expired' | 'other-tab';

export interface AuthState {
  status: AuthStatus;
  /** JWT access token. Lives only in memory, never in storage. */
  accessToken: string | null;
  /** Access token expiry on this device's clock (epoch milliseconds), see {@link localExpiry}. */
  expiresAt: number | null;
  user: CurrentUser | null;
  signOutReason: SignOutReason | null;
  /**
   * Incremented whenever the session ends. A refresh that was already on its way when the user signed out compares
   * it before applying its result, so it cannot bring the signed-out session back.
   */
  generation: number;

  setSession: (auth: AuthResponse) => void;
  setUser: (user: CurrentUser) => void;
  patchUser: (patch: Partial<CurrentUser>) => void;
  clear: (reason?: SignOutReason | null) => void;
}

/** Reads `iat`/`exp` (seconds) of a JWT without validating it (the API issued it). */
function tokenTimes(token: string): { iat: number; exp: number } | null {
  const payload = token.split('.')[1];
  if (!payload) return null;
  try {
    const json = atob(payload.replace(/-/gu, '+').replace(/_/gu, '/'));
    const claims = JSON.parse(json) as { iat?: unknown; exp?: unknown };
    return typeof claims.iat === 'number' && typeof claims.exp === 'number' && claims.exp > claims.iat
      ? { iat: claims.iat, exp: claims.exp }
      : null;
  } catch {
    return null;
  }
}

/**
 * The access token's expiry on this device's clock: its lifetime (`exp - iat`) counted from now, the moment the
 * response arrived. The server's absolute `expiresAt` is used only for tokens without those claims — a PC clock that
 * runs ahead would otherwise make every new token look expired and trigger a refresh storm.
 */
export function localExpiry(auth: AuthResponse, receivedAt = Date.now()): number {
  const times = tokenTimes(auth.accessToken);
  return times ? receivedAt + (times.exp - times.iat) * 1000 : Date.parse(auth.expiresAt);
}

export const useAuthStore = create<AuthState>()((set) => ({
  status: 'unknown',
  accessToken: null,
  expiresAt: null,
  user: null,
  signOutReason: null,
  generation: 0,

  setSession: (auth) =>
    set({
      status: 'authenticated',
      accessToken: auth.accessToken,
      expiresAt: localExpiry(auth),
      user: auth.user,
      signOutReason: null,
    }),
  setUser: (user) => set({ user }),
  patchUser: (patch) => set((state) => (state.user ? { user: { ...state.user, ...patch } } : {})),
  clear: (reason = null) =>
    set((state) => ({
      status: 'anonymous',
      accessToken: null,
      expiresAt: null,
      user: null,
      signOutReason: reason,
      generation: state.generation + 1,
    })),
}));

export const getAuthState = (): AuthState => useAuthStore.getState();
